import { ApolloError } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import {
  VALID_PACKAGE_TABS,
  VALID_VIEW_FILTERS,
  type DataStructureTabId,
  type PackageTab,
  type ViewFilter,
} from "./Settings/components/DataStructure/types";

export function parseViewFilter(
  search: string,
  defaultFilter: ViewFilter = "active",
  paramName: string = "view",
): ViewFilter {
  const raw = new URLSearchParams(search).get(paramName);
  return VALID_VIEW_FILTERS.includes(raw as ViewFilter) ? (raw as ViewFilter) : defaultFilter;
}

export function parseDataStructureType(
  search: string,
  validTabs: readonly DataStructureTabId[],
  defaultType: DataStructureTabId = "gl",
): DataStructureTabId {
  const raw = new URLSearchParams(search).get("type");
  if (raw && validTabs.includes(raw as DataStructureTabId)) {
    return raw as DataStructureTabId;
  }
  return defaultType;
}

export const isValidPackageTab = (tab: string | undefined): tab is PackageTab =>
  tab !== undefined && VALID_PACKAGE_TABS.includes(tab as PackageTab);

export const caseInsensitiveComparator = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

export const formatUserName = (
  user: { firstName?: string | null; lastName?: string | null } | null | undefined,
): string | null => [user?.firstName, user?.lastName].filter(Boolean).join(" ") || null;

type FormatDateFn = (date: string, format: string) => string;

export const formatUpdatedByLabel = (
  date: string | null | undefined,
  userName: string | null | undefined,
  formatDate: FormatDateFn,
): string => {
  const formattedDate = date ? formatDate(date, "MMM d, yyyy 'at' h:mm a") : "";
  const name = userName?.trim() ?? "";
  if (formattedDate && name) return `${formattedDate} by ${name}`;
  return formattedDate || name || "-";
};

export const formatArchivedByLabel = (
  date: string | null | undefined,
  userName: string | null | undefined,
  formatDate: FormatDateFn,
): string => {
  const formattedDate = date ? formatDate(date, "MMM d, yyyy") : "";
  const name = userName?.trim() ?? "";
  if (name && formattedDate) return `${name} on ${formattedDate}`;
  return formattedDate || name || "-";
};

export const dateFieldComparator = <T>(
  getDate: (data: T | undefined) => string | null | undefined,
) => {
  return (
    valueA: string,
    valueB: string,
    nodeA?: AgGrid.IRowNode<T>,
    nodeB?: AgGrid.IRowNode<T>,
  ): number => {
    if (!nodeA?.data || !nodeB?.data) {
      return String(valueA ?? "").localeCompare(String(valueB ?? ""));
    }
    const dateA = getDate(nodeA.data) ?? "";
    const dateB = getDate(nodeB.data) ?? "";
    return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
  };
};

const UNAUTHORIZED_CODES: ReadonlySet<string> = new Set([
  "UNAUTHORIZED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
]);

export function isUnauthorizedError(error: unknown): boolean {
  if (!(error instanceof ApolloError)) {
    return false;
  }

  const hasUnauthorizedGraphQLError = error.graphQLErrors.some((gqlError) => {
    const code = gqlError.extensions?.code;
    const errorCode = gqlError.extensions?.error_code;
    return (
      (typeof code === "string" && UNAUTHORIZED_CODES.has(code)) ||
      errorCode === "STAFF_UNAUTHENTICATED_ACCESS"
    );
  });

  if (hasUnauthorizedGraphQLError) {
    return true;
  }

  const networkError = error.networkError;
  return (
    networkError !== null &&
    networkError !== undefined &&
    "statusCode" in networkError &&
    (networkError as { statusCode: number }).statusCode === 401
  );
}
